import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { DashboardSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2,
  TrendingUp,
  FileText,
  AlertCircle,
  DollarSign,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Link as LinkIcon,
  Users,
  Send,
  Trophy,
  Zap,
  FolderOpen,
  BarChart3,
  UserCheck,
  Timer,
  Headset,
  RefreshCw,
  Coins,
  HardHat,
  Layers,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  FileWarning,
} from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/demo-data";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessPage } from "@/lib/rbac";

const ACTION_LABELS: Record<string, string> = {
  PROJECT_CREATE: "Projet créé",
  PROJECT_UPDATE: "Projet mis à jour",
  PROJECT_SET_OUTCOME: "Résultat projet",
  PARTNER_ASSIGNED: "Partenaire assigné",
  TENDER_QUALIFY: "AO qualifié",
  SERVICE_INVOICE_CREATE: "Facture créée",
  SERVICE_INVOICE_PAID: "Facture payée",
  SERVICE_INVOICE_SENT: "Facture envoyée",
  SUCCESS_FEE_GENERATE: "Success fee généré",
  SUCCESS_FEE_PAID: "Success fee payé",
  ASSISTANCE_ORDER_CREATE: "Assistance créée",
  ASSISTANCE_ORDER_PAID: "Assistance payée",
  CHECKLIST_INIT_STANDARD: "Checklist initialisée",
  COMPANY_UPDATE: "Entreprise mise à jour",
};

function pctChange(current: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((current - prev) / prev) * 100);
}

function TrendBadge({ current, prev }: { current: number; prev: number }) {
  const pct = pctChange(current, prev);
  if (pct === null) return null;
  if (pct > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-success font-medium">
        <ArrowUpRight className="h-3 w-3" />+{pct}%
      </span>
    );
  if (pct < 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-destructive font-medium">
        <ArrowDownRight className="h-3 w-3" />{pct}%
      </span>
    );
  return <span className="text-xs text-muted-foreground">=</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const canSeeBilling = canAccessPage(user?.staffRole, "BILLING");
  const canSeeProjects = canAccessPage(user?.staffRole, "PROJECTS");
  const canSeeTenders = canAccessPage(user?.staffRole, "TENDERS");
  const canSeeSupport = canAccessPage(user?.staffRole, "SUPPORT");
  const canSeeGoQueue = canAccessPage(user?.staffRole, "GO_QUEUE");
  const canSeeTravauxBTP = canAccessPage(user?.staffRole, "TRAVAUXBTP_DASHBOARD");

  const { data: stats, isLoading, isError, refetch: refetchStats } = trpc.admin.dashboard.stats.useQuery();
  const { data: goQueue, isLoading: goQueueLoading } = trpc.admin.dashboard.recentGoQueue.useQuery({ limit: 5 }, { enabled: canSeeGoQueue });
  const { data: recentActivity, isLoading: activityLoading } = trpc.admin.dashboard.recentActivity.useQuery({ limit: 10 });
  const { data: connectors, isLoading: connectorsLoading } = trpc.admin.dashboard.connectors.useQuery();
  const { data: reporting, isLoading: reportingLoading } = trpc.admin.dashboard.reporting.useQuery(undefined, {
    enabled: canSeeProjects,
  });
  const { data: travauxbtpData } = trpc.admin.travauxbtp.getDashboard.useQuery(undefined, {
    enabled: canSeeTravauxBTP,
  });
  const { data: travauxbtpMetrics } = trpc.admin.travauxbtp.getMetrics.useQuery(
    { period: "30d" as any },
    { enabled: canSeeTravauxBTP }
  );
  const { data: rotationMonitoring } = trpc.admin.travauxbtp.getRotationMonitoring.useQuery(
    undefined,
    { enabled: canSeeTravauxBTP }
  );
  const { data: travauxbtpActivity } = trpc.admin.travauxbtp.recentActivity.useQuery(
    { limit: 15 },
    { enabled: canSeeTravauxBTP }
  );
  const { data: unreadMessages } = trpc.messaging.getUnreadCount.useQuery();
  const { data: salesDashboard } = trpc.sales.getDashboard.useQuery();
  const { data: churnData } = trpc.admin.churnRisk.getAtRiskCompanies.useQuery({});
  const { data: pendingArtisans } = trpc.admin.travauxbtp.listPendingArtisans.useQuery(
    { page: 1, limit: 1 },
    { enabled: canSeeTravauxBTP }
  );
  const { data: disputes } = trpc.admin.disputes.list.useQuery(
    { page: 1, limit: 1, status: "OPEN" }
  );
  const { data: docsExpData } = trpc.admin.travauxbtp.listAllDocuments.useQuery(
    { page: 1, limit: 1 },
    { enabled: canSeeTravauxBTP }
  );
  const { data: supportStats } = trpc.admin.support.stats.useQuery();
  const { data: supportAnalytics } = trpc.admin.support.getAnalytics.useQuery(
    { period: "30d" },
    { enabled: canSeeSupport }
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold">Dashboard OPS</h1>
          <p className="text-muted-foreground">Vue unifiée multi-produits — TravauxBTP, SaaS, Commercial, Support</p>
        </div>

        {isLoading ? (
          <DashboardSkeleton />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <AlertCircle className="h-12 w-12 text-destructive/40" />
            <p className="text-muted-foreground font-medium">Erreur de chargement du dashboard</p>
            <Button variant="outline" onClick={() => refetchStats()}>
              <RefreshCw className="h-4 w-4 mr-2" />Réessayer
            </Button>
          </div>
        ) : (
          <>
            {/* ═══ SECTION 1 — VUE GLOBALE ═══ */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-warning" />
                Vue globale
              </h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <Link to="/companies">
                  <Card className="hover:bg-accent cursor-pointer transition-colors">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total entreprises</CardTitle>
                      <Building2 className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats?.companiesCount || 0}</div>
                      <p className="text-xs text-muted-foreground">{stats?.activeCompaniesCount || 0} actives</p>
                    </CardContent>
                  </Card>
                </Link>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">CA Service (mois)</CardTitle>
                    <DollarSign className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency(stats?.serviceRevenue || 0)}
                    </div>
                    {travauxbtpData && (
                      <p className="text-xs text-muted-foreground">
                        + {travauxbtpData.creditsSoldMonth.toLocaleString("fr-FR")} crédits TravauxBTP
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Conversions du mois</CardTitle>
                    <TrendingUp className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-success">
                      {salesDashboard?.monthlyConversions?.at(-1)?.won ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      sur {salesDashboard?.monthlyConversions?.at(-1)?.leads ?? 0} leads
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Taux de churn</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${(churnData?.companies?.length ?? 0) > 0 ? "text-warning" : ""}`}>
                      {churnData?.companies?.length ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      entreprises à risque
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ═══ SECTION 2 — TRAVAUXBTP (Priorité #1) ═══ */}
            {canSeeTravauxBTP && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-success" />
                  TravauxBTP
                  <Badge variant="outline" className="text-xs font-normal">Priorité #1</Badge>
                </h2>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  <Link to="/private-projects">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Projets publiés</CardTitle>
                        <FileText className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {travauxbtpData?.projectFunnel.published || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">En attente de matching</p>
                      </CardContent>
                    </Card>
                  </Link>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Artisans actifs</CardTitle>
                      <Users className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-primary">
                        {travauxbtpData?.activeArtisans || 0}
                      </div>
                      <p className="text-xs text-muted-foreground">≥ 1 achat / 30j</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Crédits vendus</CardTitle>
                      <Coins className="h-4 w-4 text-warning" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {travauxbtpData?.creditsSoldMonth.toLocaleString("fr-FR") || 0}
                      </div>
                      {travauxbtpData && (
                        <TrendBadge
                          current={travauxbtpData.creditsSoldMonth}
                          prev={travauxbtpData.creditsSoldPrevMonth}
                        />
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Taux acceptation</CardTitle>
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-success">
                        {travauxbtpData?.acceptanceRate || 0}%
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {travauxbtpData?.acceptedQuotes || 0}/{travauxbtpData?.totalQuotes || 0} devis
                      </p>
                    </CardContent>
                  </Card>

                  <Link to="/travauxbtp/artisans">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Modération</CardTitle>
                        <UserCheck className="h-4 w-4 text-warning" />
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${(pendingArtisans?.total ?? 0) > 0 ? "text-warning" : ""}`}>
                          {pendingArtisans?.total ?? 0}
                        </div>
                        <p className="text-xs text-muted-foreground">Artisans en attente</p>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/disputes">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Litiges</CardTitle>
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${(disputes?.stats?.open ?? 0) > 0 ? "text-destructive" : ""}`}>
                          {disputes?.stats ? disputes.stats.reported + disputes.stats.open + disputes.stats.inMediation + disputes.stats.escalated : 0}
                        </div>
                        <p className="text-xs text-muted-foreground">Ouverts</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>

                {/* Rotation monitoring cards */}
                {rotationMonitoring && (
                  <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Taux de remplissage</CardTitle>
                        <BarChart3 className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{Math.round(rotationMonitoring.avgFillRate * 100)}%</div>
                        <p className="text-xs text-muted-foreground">
                          {rotationMonitoring.projectsComplete} remplis / {rotationMonitoring.projectsWaiting} en attente
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Temps moyen remplissage</CardTitle>
                        <Timer className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {rotationMonitoring.avgTimeToFillHours != null ? `${rotationMonitoring.avgTimeToFillHours}h` : "N/A"}
                        </div>
                        <p className="text-xs text-muted-foreground">pour atteindre 3 artisans</p>
                      </CardContent>
                    </Card>
                    <Link to="/private-projects?filter=stale">
                      <Card className="hover:bg-accent cursor-pointer transition-colors">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Projets stagnants</CardTitle>
                          <AlertTriangle className="h-4 w-4 text-warning" />
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${rotationMonitoring.staleProjects.length > 0 ? "text-warning" : ""}`}>
                            {rotationMonitoring.staleProjects.length}
                          </div>
                          <p className="text-xs text-muted-foreground">&gt;48h sans 3 artisans</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Alertes rotation</CardTitle>
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${rotationMonitoring.rotationAlerts.length > 0 ? "text-destructive" : ""}`}>
                          {rotationMonitoring.rotationAlerts.length}
                        </div>
                        <p className="text-xs text-muted-foreground">pros avec &gt;2x leads moyenne</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* TravauxBTP metrics cards */}
                {travauxbtpMetrics && (
                  <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Projets (30j)</CardTitle>
                        <FolderOpen className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(travauxbtpMetrics as any).projectsCreated ?? 0}</div>
                        <p className="text-xs text-muted-foreground">nouveaux projets privés</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Déblocages (30j)</CardTitle>
                        <Coins className="h-4 w-4 text-success" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-success">{(travauxbtpMetrics as any).totalUnlocks ?? 0}</div>
                        <p className="text-xs text-muted-foreground">leads débloqués</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Remboursements (30j)</CardTitle>
                        <RefreshCw className="h-4 w-4 text-destructive" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-destructive">{(travauxbtpMetrics as any).totalRefunds ?? 0}</div>
                        <p className="text-xs text-muted-foreground">crédits remboursés</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Artisans (30j)</CardTitle>
                        <HardHat className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(travauxbtpMetrics as any).artisansRegistered ?? 0}</div>
                        <p className="text-xs text-muted-foreground">nouveaux inscrits</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Documents expiration */}
                {docsExpData?.stats && (docsExpData.stats.expired > 0 || docsExpData.stats.expiringIn30Days > 0) && (
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <Link to="/travauxbtp/documents">
                      <Card className="hover:bg-accent cursor-pointer transition-colors border-destructive/30">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Docs expirés</CardTitle>
                          <FileWarning className="h-4 w-4 text-destructive" />
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${docsExpData.stats.expired > 0 ? "text-destructive" : ""}`}>
                            {docsExpData.stats.expired}
                          </div>
                          <p className="text-xs text-muted-foreground">documents à renouveler</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/travauxbtp/documents">
                      <Card className="hover:bg-accent cursor-pointer transition-colors border-orange-500/30">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Expirent &lt;30j</CardTitle>
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${docsExpData.stats.expiringIn30Days > 0 ? "text-orange-500" : ""}`}>
                            {docsExpData.stats.expiringIn30Days}
                          </div>
                          <p className="text-xs text-muted-foreground">rappels à envoyer</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/travauxbtp/documents">
                      <Card className="hover:bg-accent cursor-pointer transition-colors border-yellow-500/30">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Expirent &lt;90j</CardTitle>
                          <Clock className="h-4 w-4 text-yellow-600" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-yellow-600">
                            {docsExpData.stats.expiringIn90Days}
                          </div>
                          <p className="text-xs text-muted-foreground">à surveiller</p>
                        </CardContent>
                      </Card>
                    </Link>
                  </div>
                )}

                {/* Rotation activity feed */}
                {travauxbtpActivity && travauxbtpActivity.length > 0 && (
                  <Card className="mt-3">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" />
                        Activité récente TravauxBTP
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {travauxbtpActivity.map((event: any) => {
                          const icon = event.type === "LEAD_UNLOCK" ? <ArrowUpRight className="h-3.5 w-3.5 text-success" />
                            : event.type === "PRIVATE_PROJECT_CREATED" ? <FolderOpen className="h-3.5 w-3.5 text-primary" />
                            : event.type === "ARTISAN_REGISTERED" ? <HardHat className="h-3.5 w-3.5 text-primary" />
                            : event.type === "DISPUTE_OPENED" ? <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                            : event.type === "CREDIT_PURCHASE" ? <Coins className="h-3.5 w-3.5 text-success" />
                            : <Layers className="h-3.5 w-3.5 text-muted-foreground" />;
                          return (
                            <div key={`${event.type}-${event.id}`} className="flex items-start gap-2 text-sm py-1 border-b border-border/50 last:border-0">
                              <div className="mt-0.5 shrink-0">{icon}</div>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{event.label}</span>
                                {event.detail && (
                                  <span className="text-muted-foreground ml-1">— {event.detail}</span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                {formatDate(event.createdAt)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* ═══ SECTION 3 — SaaS ═══ */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                SaaS
              </h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Montant gagné</CardTitle>
                    <Trophy className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency(stats?.totalAwardedAmount || 0)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Taux de gain</CardTitle>
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats?.winRate || 0}%</div>
                  </CardContent>
                </Card>

                <Link to="/companies?filter=expired_docs">
                  <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.companiesWithExpiredDocs || 0) > 0 ? "border-destructive/30" : ""}`}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Docs expirés</CardTitle>
                      <Clock className="h-4 w-4 text-destructive" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-destructive">
                        {stats?.companiesWithExpiredDocs || 0}
                      </div>
                      <p className="text-xs text-muted-foreground">Entreprises à relancer</p>
                    </CardContent>
                  </Card>
                </Link>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">MRR (SaaS)</CardTitle>
                    <DollarSign className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {salesDashboard
                        ? `${Math.round(
                            salesDashboard.leads
                              .filter((l: any) => l.status === "GAGNE")
                              .reduce((s: number, l: any) => s + l.estimatedValue / 12, 0)
                          ).toLocaleString("fr-FR")} €`
                        : "0 €"}
                    </div>
                    <p className="text-xs text-muted-foreground">récurrent mensuel</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ═══ SECTION 4 — COMMERCIAL ═══ */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-warning" />
                Commercial
              </h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {canSeeTenders && (
                  <Link to="/tenders">
                    <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.tendersToQualify || 0) > 0 ? "border-warning/30" : ""}`}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">AO à qualifier</CardTitle>
                        <FileText className="h-4 w-4 text-warning" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-warning">{stats?.tendersToQualify || 0}</div>
                      </CardContent>
                    </Card>
                  </Link>
                )}

                {canSeeGoQueue && (
                  <Link to="/go-queue">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">GO Queue</CardTitle>
                        <Zap className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-primary">
                          {goQueue?.length || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">En attente</p>
                      </CardContent>
                    </Card>
                  </Link>
                )}

                {canSeeProjects && (
                  <>
                    <Link to="/projects?filter=in_progress">
                      <Card className="hover:bg-accent cursor-pointer transition-colors">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Projets en cours</CardTitle>
                          <FolderOpen className="h-4 w-4 text-primary" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">{stats?.projectsInProgress || 0}</div>
                          <p className="text-xs text-muted-foreground">
                            {stats?.projectsSubmittedThisWeek || 0} soumis cette semaine
                          </p>
                        </CardContent>
                      </Card>
                    </Link>

                    <Link to="/projects?filter=late">
                      <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.projectsLate || 0) > 0 ? "border-destructive/30" : ""}`}>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Projets en retard</CardTitle>
                          <Clock className="h-4 w-4 text-destructive" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-destructive">{stats?.projectsLate || 0}</div>
                        </CardContent>
                      </Card>
                    </Link>
                  </>
                )}

                {canSeeBilling && (
                  <Link to="/billing">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">À facturer</CardTitle>
                        <Send className="h-4 w-4 text-warning" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-warning">
                          {(stats?.assistanceOrdersDue || 0) + (stats?.successFeesDue || 0)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency((stats?.assistanceDueTotal || 0) + (stats?.successFeeDueTotal || 0))}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                )}

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Taux conversion leads</CardTitle>
                    <TrendingUp className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-success">
                      {salesDashboard
                        ? (() => {
                            const total = salesDashboard.leads.length;
                            const won = salesDashboard.leads.filter((l: any) => l.status === "GAGNE").length;
                            return total > 0 ? `${Math.round((won / total) * 100)}%` : "0%";
                          })()
                        : "0%"}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {salesDashboard
                        ? `${salesDashboard.leads.filter((l: any) => l.status === "GAGNE").length}/${salesDashboard.leads.length} leads`
                        : ""}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ═══ SECTION 5 — SUPPORT ═══ */}
            {canSeeSupport && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Headset className="h-5 w-5 text-primary" />
                  Support
                </h2>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <Link to="/support?filter=open">
                    <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.supportTicketsOpen || 0) > 0 ? "border-primary/30" : ""}`}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Tickets ouverts</CardTitle>
                        <Headset className="h-4 w-4 text-primary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-primary">{stats?.supportTicketsOpen || 0}</div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/support?priority=URGENT">
                    <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.supportTicketsUrgent || 0) > 0 ? "border-destructive/30" : ""}`}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Urgents</CardTitle>
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-destructive">{stats?.supportTicketsUrgent || 0}</div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/messages">
                    <Card className="hover:bg-accent cursor-pointer transition-colors">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Messages non lus</CardTitle>
                        <MessageSquare className="h-4 w-4 text-warning" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-warning">
                          {(unreadMessages as any)?.count || 0}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Temps réponse moyen</CardTitle>
                      <Timer className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {supportAnalytics?.avgFirstResponseTimeHours != null
                          ? `${supportAnalytics.avgFirstResponseTimeHours}h`
                          : "N/A"}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        1ère réponse (30j) — {supportStats?.totalOpen ?? 0} ouverts
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* ═══ PRODUCTION, GO QUEUE, ACTIVITÉ ═══ */}
            {canSeeProjects && reportingLoading && (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i}><CardContent className="p-5"><Skeleton className="h-4 w-24 mb-3" /><Skeleton className="h-8 w-16" /></CardContent></Card>
                ))}
              </div>
            )}
            {canSeeProjects && reporting && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-pipeline-demo" />
                  Production & Qualité
                </h2>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Délai moyen production</CardTitle>
                      <Timer className="h-4 w-4 text-pipeline-demo" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reporting.avgProductionDays}j</div>
                      <p className="text-xs text-muted-foreground">
                        Sur {reporting.submittedSampleSize} projet(s) soumis
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Soumis ce mois</CardTitle>
                      <Send className="h-4 w-4 text-success" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reporting.submittedThisMonth}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Partenaires actifs</CardTitle>
                      <UserCheck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reporting.partnerLoad.length}</div>
                      <p className="text-xs text-muted-foreground">
                        {reporting.partnerLoad.reduce((s: number, p: any) => s + p.activeProjects, 0)} projets assignés
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Répartition statuts</CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {Object.entries(reporting.statusDistribution)
                          .filter(([, count]) => (count as number) > 0)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .slice(0, 4)
                          .map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">{status}</span>
                              <span className="font-medium">{count as number}</span>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {(() => {
                  const won = reporting.statusDistribution?.WON || 0;
                  const lost = reporting.statusDistribution?.LOST || 0;
                  const totalClosed = won + lost;
                  if (totalClosed === 0) return null;
                  const rate = Math.round((won / totalClosed) * 100);
                  return (
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Taux de succès AO</CardTitle>
                          <Trophy className="h-4 w-4 text-success" />
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${rate >= 50 ? "text-success" : "text-warning"}`}>{rate}%</div>
                          <p className="text-xs text-muted-foreground">{won} gagné(s) / {totalClosed} clôturé(s)</p>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })()}

                {reporting.partnerLoad.length > 0 && (
                  <div className="mt-3">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Charge partenaires</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                          {reporting.partnerLoad
                            .sort((a: any, b: any) => b.activeProjects - a.activeProjects)
                            .map((p: any) => (
                              <div key={p.name} className="flex items-center justify-between p-2 border rounded text-sm">
                                <span className="font-medium truncate">{p.name}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant={p.activeProjects >= p.capacity ? "destructive" : "outline"} className="text-xs">
                                    {p.activeProjects}{p.capacity > 0 ? `/${p.capacity}` : ""} projets
                                  </Badge>
                                </div>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* ═══ GO QUEUE + ACTIVITÉ + CONNECTEURS ═══ */}
            <div className="grid gap-6 lg:grid-cols-3">
              {canSeeGoQueue && <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">GO Queue (urgents)</CardTitle>
                  <Link to="/go-queue">
                    <Button variant="ghost" size="sm">Voir tout</Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {goQueueLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                          <Skeleton className="h-6 w-8 ml-2" />
                        </div>
                      ))}
                    </div>
                  ) : goQueue && goQueue.length > 0 ? (
                    <div className="space-y-3">
                      {goQueue.map((match: any) => {
                        if (!match.Tender) return null;
                        const deadline = new Date(match.Tender.deadlineAt);
                        const now = new Date();
                        const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                        const isUrgent = daysLeft <= 7;

                        return (
                          <div
                            key={match.id}
                            className={`flex items-center justify-between p-3 border rounded-lg ${isUrgent ? "border-destructive/30 bg-destructive/5" : ""}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{match.Company?.name || "N/A"}</p>
                              <p className="text-xs text-muted-foreground truncate">{match.Tender.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(match.Tender.deadlineAt)}
                                </p>
                                {isUrgent && (
                                  <Badge variant="destructive" className="text-xs">
                                    {daysLeft <= 0 ? "EN RETARD" : `${daysLeft}j`}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <span className="ml-2 px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium">
                              {match.score}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucun GO urgent</p>
                  )}
                </CardContent>
              </Card>}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Activité récente</CardTitle>
                </CardHeader>
                <CardContent>
                  {activityLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : recentActivity && recentActivity.length > 0 ? (
                    <div className="space-y-3">
                      {recentActivity.map((log: any) => (
                        <div key={log.id} className="flex items-start gap-3 text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{ACTION_LABELS[log.action] || log.action}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.User?.email || "Système"} &bull; {formatDate(log.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune activité</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <LinkIcon className="h-4 w-4" />
                    Ingestion AO
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 border rounded-lg">
                      <div className="text-2xl font-bold">{stats?.tendersLast24h || 0}</div>
                      <p className="text-xs text-muted-foreground">AO (24h)</p>
                    </div>
                    <div className="text-center p-3 border rounded-lg">
                      <div className="text-2xl font-bold">{stats?.tendersLast7Days || 0}</div>
                      <p className="text-xs text-muted-foreground">AO (7j)</p>
                    </div>
                  </div>

                  {connectorsLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between p-2 border rounded">
                          <div className="space-y-1">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                          <Skeleton className="h-5 w-16" />
                        </div>
                      ))}
                    </div>
                  ) : connectors && connectors.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun connecteur configuré</p>
                  ) : (
                    <div className="space-y-2">
                      {connectors?.map((connector) => (
                        <div key={connector.name} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-medium text-sm">{connector.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {connector.lastRunAt
                                ? `Dernier: ${formatDate(connector.lastRunAt)}`
                                : "Jamais"}
                            </div>
                          </div>
                          <Badge
                            variant={
                              connector.status === "SUCCESS"
                                ? "default"
                                : connector.status === "FAILED"
                                  ? "destructive"
                                  : "secondary"
                            }
                          >
                            {connector.status || "N/A"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {canSeeTenders && (stats?.tendersLowQuality || 0) > 0 && (
                    <Link to="/tenders?filter=low">
                      <div className="flex items-center justify-between p-3 border border-warning/30 rounded-lg bg-warning/5 mt-2">
                        <div>
                          <p className="font-medium text-sm text-warning">AO basse qualité</p>
                          <p className="text-xs text-muted-foreground">Quality &lt; 60</p>
                        </div>
                        <span className="text-lg font-bold text-warning">{stats?.tendersLowQuality}</span>
                      </div>
                    </Link>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
